import React, { useState } from 'react';
import { Card, Table, Button, Space, Tag, Tooltip, Modal, message } from 'antd';
import { EditOutlined, DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { Competition, CompetitionWinner } from '../../../core/competition/types';
import { createOrUpdateCompetitionWinner, deleteCompetitionWinner } from '../../../core/competition/competitionApi';
import { placeLevelOptions, gradeOptions } from '../../../core/utils/helpers';
import CreateCompetitionWinnerModal from '../CreateCompetitionWinnerModal';
import { AppDispatch } from '../../../core/types';

interface CompetitionWinnersProps {
    competition: Competition | null;
}

const CompetitionWinners: React.FC<CompetitionWinnersProps> = ({
    competition,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingWinner, setEditingWinner] = useState<CompetitionWinner | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    if (!competition) {
        return <div>Konkursas nerastas</div>;
    }

    const handleCreateWinner = () => {
        setEditingWinner(null);
        setIsModalVisible(true);
    };

    const handleEditWinner = (winner: CompetitionWinner) => {
        setEditingWinner(winner);
        setIsModalVisible(true);
    };

    const handleDeleteWinner = (winner: CompetitionWinner) => {
        Modal.confirm({
            title: 'Ar tikrai norite ištrinti šį nugalėtoją?',
            content: `Nugalėtojas: ${winner.participant_name}`,
            okText: 'Taip',
            cancelText: 'Ne',
            onOk: async () => {
                try {
                    setIsLoading(true);
                    await dispatch(deleteCompetitionWinner({
                        winner_id: winner.id!,
                    })).unwrap();

                    message.success('Nugalėtojas sėkmingai ištrintas');
                } catch (error) {
                    message.error('Klaida trinant nugalėtoją');
                } finally {
                    setIsLoading(false);
                }
            },
        });
    };

    const handleSubmitWinner = async (values: Partial<CompetitionWinner>) => {
        try {
            setIsLoading(true);
            const result = await dispatch(createOrUpdateCompetitionWinner(values)).unwrap();

            if (result.success && result.competition) {
                message.success(editingWinner ? 'Nugalėtojas sėkmingai atnaujintas' : 'Nugalėtojas sėkmingai sukurtas');
                setIsModalVisible(false);
            }
        } catch (error) {
            message.error('Klaida išsaugant nugalėtoją');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelModal = () => {
        setIsModalVisible(false);
        setEditingWinner(null);
    };

    const getPlaceLevelColor = (level: string) => {
        const place = placeLevelOptions.find(p => p.value === level);
        return place?.color || '#8c8c8c';
    };

    const getGradeLabel = (grade: number) => {
        const gradeInfo = gradeOptions.find(g => g.value === grade);
        return gradeInfo?.label || `${grade} klasė`;
    };

    const columns = [
        {
            title: 'Nugalėtojo vardas',
            dataIndex: 'participant_name',
            key: 'participant_name',
            width: 200,
        },
        {
            title: 'Mokykla',
            dataIndex: 'school_name',
            key: 'school_name',
            width: 250,
            render: (school_name: any) => school_name || '-',
        },
        {
            title: 'Klasė',
            dataIndex: 'grade',
            key: 'grade',
            width: 120,
            render: (grade: number) => getGradeLabel(grade),
        },
        {
            title: 'Vieta',
            dataIndex: 'place_level',
            key: 'place_level',
            width: 120,
            render: (level: string) => {
                const place = placeLevelOptions.find(p => p.value === level);
                return (
                    <Tag color={getPlaceLevelColor(level)}>
                        {place?.label || level}
                    </Tag>
                );
            },
        },
        {
            title: 'Mokytojai',
            dataIndex: 'teachers',
            key: 'teachers',
            width: 200,
            render: (teachers: any[]) => {
                if (!teachers || teachers.length === 0) return '-';
                return (
                    <Tooltip title={teachers.map(t => `${t.first_name} ${t.last_name}`).join(', ')}>
                        <div>
                            {teachers.slice(0, 2).map(teacher => (
                                <div key={teacher.id}>
                                    {teacher.first_name} {teacher.last_name}
                                    {teacher.pivot?.subject && ` (${teacher.pivot.subject})`}
                                </div>
                            ))}
                            {teachers.length > 2 && (
                                <div>+{teachers.length - 2} daugiau</div>
                            )}
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Pastabos',
            dataIndex: 'notes',
            key: 'notes',
            width: 200,
            render: (notes: string) => {
                if (!notes) return '-';
                return (
                    <Tooltip title={notes}>
                        <div style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: 200,
                        }}>
                            {notes}
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Veiksmai',
            key: 'actions',
            width: 120,
            render: (_: any, record: CompetitionWinner) => (
                <Space>
                    <Tooltip title="Redaguoti">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditWinner(record)}
                            size="small" />
                    </Tooltip>
                    <Tooltip title="Ištrinti">
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteWinner(record)}
                            size="small" />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Card
            title="Laimėtojai"
            extra={(
                <Button
                    type="primary"
                    icon={<PlusCircleOutlined />}
                    onClick={handleCreateWinner}
                    loading={isLoading}>
                    Pridėti nugalėtoją
                </Button>
            )}>
            <Table
                dataSource={competition.winners || []}
                columns={columns}
                rowKey="id"
                loading={isLoading}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} iš ${total} nugalėtojų`,
                }}
                scroll={{ x: 'max-content' }} />

            <CreateCompetitionWinnerModal
                visible={isModalVisible}
                onCancel={handleCancelModal}
                onSubmit={handleSubmitWinner}
                loading={isLoading}
                winner={editingWinner}
                competitionId={competition.id!} />
        </Card>
    );
};

export default CompetitionWinners;
